import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import SkuCopyRow from "../../../../../../views/components/common/SkuCopyRow";
import AppSearchDropdownMulti from "../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import { PackagingOpsItemRow } from "../../../../../../models/ProjectManagement/ProjectPackagingSwagpacks";
import { PartnerCustomerInventoryTag } from "../../../../../../models/inventory/PartnerCustomerInventoryTag";
import { PackagingOpsInwardLineMeta } from "../../../../stores/ProjectManagement/PackagingOpsStore";

interface InwardItemsPopupProps {
  open: boolean;
  items: PackagingOpsItemRow[];
  tags: PartnerCustomerInventoryTag[];
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (lineMeta: PackagingOpsInwardLineMeta[]) => void;
}

type LineFormState = {
  clientSku: string;
  packSize: string;
  boxSize: string;
  tagId: string;
  tagLabel: string;
};

export default function InwardItemsPopup({
  open,
  items,
  tags,
  isSubmitting = false,
  onClose,
  onSubmit,
}: InwardItemsPopupProps) {
  const theme = useTheme();
  const [lineForms, setLineForms] = useState<Record<string, LineFormState>>(
    {},
  );
  const [clientSkuErrors, setClientSkuErrors] = useState<
    Record<string, string>
  >({});

  const tagOptions: FilterValue[] = tags
    .filter((tag) => tag.active !== 0)
    .map((tag) => ({
      id: String(tag.tag_id),
      value: tag.tag,
    }));

  useEffect(() => {
    if (!open) return;
    const next: Record<string, LineFormState> = {};
    items.forEach((item) => {
      const matchedTag = tags.find(
        (tag) => String(tag.tag_id) === String(item.tagId || ""),
      );
      next[item.sku] = {
        clientSku: item.clientSku || item.sku,
        packSize:
          item.packSize != null && Number.isFinite(item.packSize)
            ? String(item.packSize)
            : "",
        boxSize: item.boxSize || "",
        tagId: item.tagId || "",
        tagLabel: matchedTag?.tag || item.tag || "",
      };
    });
    setLineForms(next);
    setClientSkuErrors({});
  }, [open, items, tags]);

  const updateLine = (catalogSku: string, patch: Partial<LineFormState>) => {
    setLineForms((current) => ({
      ...current,
      [catalogSku]: {
        ...current[catalogSku],
        ...patch,
      },
    }));
    if (patch.clientSku !== undefined) {
      setClientSkuErrors((current) => {
        if (!current[catalogSku]) return current;
        const next = { ...current };
        delete next[catalogSku];
        return next;
      });
    }
  };

  const validateClientSkus = (): boolean => {
    const nextErrors: Record<string, string> = {};
    const seen = new Map<string, string>();

    items.forEach((item) => {
      const clientSku = lineForms[item.sku]?.clientSku?.trim() || "";
      if (!clientSku) {
        nextErrors[item.sku] = "Client SKU is required";
        return;
      }

      const existingKey = seen.get(clientSku.toLowerCase());
      if (existingKey) {
        nextErrors[item.sku] = "Client SKU must be unique in this inward";
        nextErrors[existingKey] = "Client SKU must be unique in this inward";
        return;
      }

      seen.set(clientSku.toLowerCase(), item.sku);
    });

    setClientSkuErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (isSubmitting) return;
    if (!validateClientSkus()) {
      return;
    }

    const lineMeta: PackagingOpsInwardLineMeta[] = items.map((item) => {
      const form = lineForms[item.sku];
      const packSizeRaw = form?.packSize?.trim() || "";
      const packSize = packSizeRaw === "" ? undefined : Number(packSizeRaw);
      return {
        sku: item.sku,
        clientSku: form?.clientSku?.trim() || item.clientSku || item.sku,
        packSize:
          packSize != null && Number.isFinite(packSize) && packSize >= 0
            ? packSize
            : undefined,
        boxSize: form?.boxSize?.trim() || undefined,
        tagId: form?.tagId?.trim() || undefined,
      };
    });
    onSubmit(lineMeta);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
            position: "relative",
          },
        },
      }}
      sx={{ zIndex: 2000 }}
    >
      <Backdrop
        open={isSubmitting}
        sx={{
          position: "absolute",
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
          color: theme.palette.customColors.white,
          borderRadius: "16px",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box
        sx={{
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            <CustomTypography variant="heading4" fontWeight={700}>
              Mark Inwarded
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              Catalog SKU is sent as-is. Update client SKU, pack size, box
              size, and tag per line.
            </CustomTypography>
          </Box>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Box
                display="flex"
                flexDirection="column"
                gap={1.5}
                sx={{ maxHeight: 420, overflowY: "auto" }}
              >
                {items.map((item) => {
                  const form = lineForms[item.sku] || {
                    clientSku: item.clientSku || item.sku,
                    packSize: "",
                    boxSize: "",
                    tagId: "",
                    tagLabel: "",
                  };
                  const clientSkuError = clientSkuErrors[item.sku];
                  return (
                    <Paper
                      key={item.sku}
                      elevation={0}
                      sx={{ p: 2, borderRadius: 3 }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        gap={2}
                        flexWrap="wrap"
                        mb={1.5}
                      >
                        <Box minWidth={0} flex={1}>
                          <CustomTypography variant="body2" fontWeight={600}>
                            {item.productName || item.title || "—"}
                          </CustomTypography>
                          <Box sx={{ mt: 0.5 }}>
                            <CustomTypography
                              variant="body3"
                              color="text.secondary"
                              sx={{ display: "block", mb: 0.25 }}
                            >
                              SKU
                            </CustomTypography>
                            <SkuCopyRow sku={item.sku} fontWeight={600} />
                          </Box>
                        </Box>
                        <CustomTypography variant="body2" fontWeight={600}>
                          Qty: {item.remainingInward}
                        </CustomTypography>
                      </Box>

                      <Grid2 container spacing={1.5}>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                          <CustomTypography variant="body3" fontWeight={600}>
                            CLIENT SKU
                          </CustomTypography>
                          <Box sx={{ mt: 0.75 }}>
                            <CustomTextField
                              value={form.clientSku}
                              onChange={(e) =>
                                updateLine(item.sku, {
                                  clientSku: e.target.value,
                                })
                              }
                              width="100%"
                              placeholder="Enter client SKU"
                            />
                          </Box>
                          {clientSkuError && (
                            <CustomTypography
                              variant="body3"
                              sx={{
                                mt: 0.5,
                                color: theme.palette.error.main,
                                display: "block",
                              }}
                            >
                              {clientSkuError}
                            </CustomTypography>
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                          <CustomTypography variant="body3" fontWeight={600}>
                            PACK SIZE
                          </CustomTypography>
                          <Box sx={{ mt: 0.75 }}>
                            <CustomTextField
                              type="number"
                              value={form.packSize}
                              onChange={(e) =>
                                updateLine(item.sku, {
                                  packSize: e.target.value,
                                })
                              }
                              width="100%"
                              placeholder="Optional"
                              allowNegativeValue={false}
                              allowDecimalValue={false}
                            />
                          </Box>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                          <CustomTypography variant="body3" fontWeight={600}>
                            BOX SIZE
                          </CustomTypography>
                          <Box sx={{ mt: 0.75 }}>
                            <CustomTextField
                              value={form.boxSize}
                              onChange={(e) =>
                                updateLine(item.sku, {
                                  boxSize: e.target.value,
                                })
                              }
                              width="100%"
                              placeholder="Optional"
                            />
                          </Box>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                          <CustomTypography variant="body3" fontWeight={600}>
                            TAG
                          </CustomTypography>
                          <Box sx={{ mt: 0.75 }}>
                            <AppSearchDropdownMulti
                              isMultiSelect={false}
                              title="Select tag"
                              local
                              list={tagOptions}
                              value={
                                form.tagId
                                  ? [
                                      {
                                        id: form.tagId,
                                        value: form.tagLabel || form.tagId,
                                      },
                                    ]
                                  : []
                              }
                              handleSelect={(selected) => {
                                if (!selected.length) {
                                  updateLine(item.sku, {
                                    tagId: "",
                                    tagLabel: "",
                                  });
                                  return;
                                }
                                updateLine(item.sku, {
                                  tagId: String(selected[0].id),
                                  tagLabel: selected[0].value,
                                });
                              }}
                              fontSize="12px"
                              minWidth="100%"
                            />
                          </Box>
                        </Grid2>
                      </Grid2>
                    </Paper>
                  );
                })}
              </Box>
            </Grid2>

            <Grid2
              size={12}
              display="flex"
              justifyContent="flex-end"
              gap={1.5}
              sx={{ pt: 1 }}
            >
              <AppButton
                label="Cancel"
                onClick={handleClose}
                variant="grayscale"
                disabled={isSubmitting}
              />
              <AppButton
                label="Mark Inwarded"
                onClick={handleSubmit}
                variant="primary"
                disabled={items.length === 0 || isSubmitting}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
